import { Aviso } from '@/lib/types';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Loader2, Megaphone, Bell, AlertTriangle, Info, ChevronRight, Clock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { onSnapshot, collection, query, orderBy } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { sanitizeFirestoreData } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { RichText } from '@/components/ui/rich-text';

function toDateSafe(v: any): Date {
    if (!v) return new Date(0);
    if (typeof v === 'object' && typeof v.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return isNaN(d.getTime()) ? new Date(0) : d;
}

const avisoBg = [
    'from-primary/20 to-primary/5 border-primary/25',
    'from-amber-500/20 to-amber-500/5 border-amber-500/25',
    'from-sky-500/20 to-sky-500/5 border-sky-500/25',
    'from-violet-500/20 to-violet-500/5 border-violet-500/25',
    'from-rose-500/20 to-rose-500/5 border-rose-500/25',
    'from-emerald-500/20 to-emerald-500/5 border-emerald-500/25',
];

const avisoAccent = [
    'text-primary bg-primary/10',
    'text-amber-400 bg-amber-500/10',
    'text-sky-400 bg-sky-500/10',
    'text-violet-400 bg-violet-500/10',
    'text-rose-400 bg-rose-500/10',
    'text-emerald-400 bg-emerald-500/10',
];

export default function AvisosPage() {
    const [avisos, setAvisos] = useState<Aviso[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<string | null>(null);

    useEffect(() => {
        const q = query(collection(db, 'avisos'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const avisosData = snapshot.docs.map(doc =>
                sanitizeFirestoreData({ id: doc.id, ...doc.data() })
            ) as Aviso[];
            setAvisos(avisosData);
            setLoading(false);
        }, (err) => {
            console.error(err);
            setError('No se pudieron cargar los avisos.');
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="relative overflow-hidden rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-8">
                <div className="absolute -top-8 -right-8 h-40 w-40 rounded-full bg-primary/8 blur-3xl" />
                <div className="absolute -bottom-6 -left-6 h-32 w-32 rounded-full bg-primary/6 blur-2xl" />
                <div className="relative flex items-center gap-5">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-primary/25 bg-primary/10 shadow-lg shadow-primary/10">
                        <Megaphone className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                        <h1 className="font-headline text-4xl font-bold tracking-tight">Tablón de Avisos</h1>
                        <p className="mt-1 text-muted-foreground">
                            Comunicados oficiales del servidor Jartiland
                        </p>
                    </div>
                    {avisos.length > 0 && (
                        <div className="ml-auto flex h-10 min-w-10 items-center justify-center rounded-full border border-primary/30 bg-primary/10 px-3 text-sm font-bold text-primary">
                            {avisos.length}
                        </div>
                    )}
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex h-52 items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : error ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-destructive/20 bg-destructive/5 py-16">
                    <AlertTriangle className="h-10 w-10 text-destructive/70" />
                    <p className="font-medium text-destructive">{error}</p>
                </div>
            ) : avisos.length === 0 ? (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-white/8 py-20 text-muted-foreground"
                >
                    <Bell className="h-12 w-12 opacity-30" />
                    <p className="text-lg font-medium">No hay avisos en este momento</p>
                    <p className="text-sm opacity-60">Los comunicados oficiales aparecerán aquí</p>
                </motion.div>
            ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    <AnimatePresence>
                        {avisos.map((aviso, idx) => {
                            const colorIdx = idx % avisoBg.length;
                            const isOpen = expanded === aviso.id;
                            const date = toDateSafe(aviso.createdAt);
                            const isRecent = Date.now() - date.getTime() < 1000 * 60 * 60 * 72;

                            return (
                                <motion.div
                                    key={aviso.id}
                                    layout
                                    initial={{ opacity: 0, y: 16, scale: 0.97 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    transition={{ delay: idx * 0.05, type: 'spring', stiffness: 260, damping: 22 }}
                                    whileHover={{ y: -4 }}
                                    className={cn(
                                        'group relative cursor-pointer overflow-hidden rounded-2xl border bg-gradient-to-br transition-shadow duration-300 hover:shadow-xl',
                                        avisoBg[colorIdx]
                                    )}
                                    onClick={() => setExpanded(isOpen ? null : aviso.id)}
                                >
                                    {/* Top accent bar */}
                                    <div className={cn('h-1 w-full', avisoAccent[colorIdx].split(' ')[1].replace('/10', '/60'))} />

                                    <div className="p-5">
                                        {/* Icon + badges row */}
                                        <div className="mb-4 flex items-start justify-between gap-3">
                                            <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl', avisoAccent[colorIdx])}>
                                                <Info className="h-5 w-5" />
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2">
                                                {isRecent && (
                                                    <span className="flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-primary">
                                                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                                                        Nuevo
                                                    </span>
                                                )}
                                                <span className="flex items-center gap-1 rounded-full bg-white/6 px-2 py-0.5 text-[0.6rem] text-muted-foreground">
                                                    <Clock className="h-2.5 w-2.5" />
                                                    {formatDistanceToNow(date, { locale: es, addSuffix: true })}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Title */}
                                        <h3 className="mb-2 font-headline text-lg font-bold leading-snug">
                                            {aviso.title}
                                        </h3>

                                        {/* Content — truncated unless expanded */}
                                        {isOpen ? (
                                            <motion.div layout className="text-sm text-muted-foreground">
                                                <RichText>{aviso.content}</RichText>
                                            </motion.div>
                                        ) : (
                                            <motion.p layout className="text-sm text-muted-foreground line-clamp-3">
                                                {aviso.content}
                                            </motion.p>
                                        )}

                                        {/* Footer */}
                                        <div className="mt-4 flex items-center justify-between border-t border-white/6 pt-3">
                                            <span className="text-xs text-muted-foreground">
                                                {format(date, "d 'de' MMMM, yyyy", { locale: es })}
                                            </span>
                                            <span className={cn(
                                                'flex items-center gap-1 text-xs font-medium transition-colors',
                                                avisoAccent[colorIdx].split(' ')[0]
                                            )}>
                                                {isOpen ? 'Cerrar' : 'Leer más'}
                                                <ChevronRight className={cn('h-3.5 w-3.5 transition-transform duration-200', isOpen && 'rotate-90')} />
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </div>
            )}
        </div>
    );
}
